/**
 * Бронювання номера — повний цикл:
 *   1. Пошук вільних номерів за датами (AJAX, inc/booking.php).
 *   2. Клік «Забронювати» на картці → форма заявки (CF7, inc/booking-form.php)
 *      з уже підставленими номером і датами.
 *
 * Доступність перевіряється ДВІЧІ: під час пошуку і ще раз на сабміті —
 * бо між цими подіями номер могли забронювати (див. inc/booking-form.php).
 */

import { __, _n, sprintf } from '@wordpress/i18n';

const formatPrice = (value) =>
	new Intl.NumberFormat('uk-UA', { maximumFractionDigits: 0 }).format(value);

const formatDate = (value) => {
	const d = new Date(value);
	if (Number.isNaN(d.getTime())) return value;
	return d.toLocaleDateString('uk-UA', { day: '2-digit', month: 'long', year: 'numeric' });
};

const escapeHtml = (str) =>
	String(str).replace(/[&<>"']/g, (c) => ({
		'&': '&amp;',
		'<': '&lt;',
		'>': '&gt;',
		'"': '&quot;',
		"'": '&#39;',
	})[c]);

function renderCard(room) {
	const meta = [
		room.area ? sprintf(__('%d m²', 'hotel'), room.area) : '',
		room.guests
			? sprintf(_n('%d guest', '%d guests', room.guests, 'hotel'), room.guests)
			: '',
	]
		.filter(Boolean)
		.join(' · ');

	// ⚠️ Картка — НЕ <a>: усередині є кнопка «Забронювати», а вкладена
	// інтерактивність усередині посилання ламає і клавіатурну навігацію,
	// і саму поведінку кліку. Тому картка — <article>, а посилання на номер
	// висить лише на фото й заголовку.
	return `
		<article class="booking-card">
			${room.thumb_html
				// thumb_html прийшов з wp_get_attachment_image (готовий <img>
				// зі srcset) — це вже екранований WP-и HTML, escapeHtml тут
				// зламав би розмітку.
				? `<a class="booking-card__media" href="${escapeHtml(room.permalink)}">${room.thumb_html}</a>`
				: ''}
			<div class="booking-card__body">
				${room.wing ? `<div class="booking-card__wing">${escapeHtml(room.wing)}</div>` : ''}
				<h3><a href="${escapeHtml(room.permalink)}">${escapeHtml(room.title)}</a></h3>
				${meta ? `<p class="booking-card__meta">${escapeHtml(meta)}</p>` : ''}
				${room.price ? `<div class="booking-card__price">${escapeHtml(sprintf(__('from %s ₴', 'hotel'), formatPrice(room.price)))}</div>` : ''}

				<button type="button" class="booking-card__cta"
					data-room-id="${escapeHtml(room.id)}"
					data-room-title="${escapeHtml(room.title)}"
					data-room-price="${escapeHtml(room.price || 0)}">
					${escapeHtml(__('Book', 'hotel'))}
				</button>
			</div>
		</article>
	`;
}

/**
 * Відкриває форму заявки: підставляє номер і дати в приховані поля,
 * малює підсумок і скролить до неї.
 */
function openRequestForm({ roomId, roomTitle, roomPrice, checkIn, checkOut, guests }) {
	const wrap = document.querySelector('.booking-request');
	if (!wrap) return;

	const form = wrap.querySelector('form');
	if (!form) return;

	const set = (name, value) => {
		const field = form.querySelector(`[name="${name}"]`);
		if (field) field.value = value;
	};

	set('booking-room-id', roomId);
	set('booking-check-in', checkIn);
	set('booking-check-out', checkOut);
	set('booking-guests', guests);

	// Підсумок: що саме бронює гість — щоб не гадав, чи ту картку натиснув.
	const summary = form.querySelector('[data-summary]');
	if (summary) {
		const nights = Math.max(
			1,
			Math.round((new Date(checkOut) - new Date(checkIn)) / 86400000)
		);
		const total = roomPrice ? roomPrice * nights : 0;

		summary.innerHTML = `
			<h3>${escapeHtml(roomTitle)}</h3>
			<dl>
				<div><dt>${escapeHtml(__('Check-in', 'hotel'))}</dt><dd>${formatDate(checkIn)}</dd></div>
				<div><dt>${escapeHtml(__('Check-out', 'hotel'))}</dt><dd>${formatDate(checkOut)}</dd></div>
				<div><dt>${escapeHtml(__('Nights', 'hotel'))}</dt><dd>${nights}</dd></div>
				<div><dt>${escapeHtml(__('Guests', 'hotel'))}</dt><dd>${escapeHtml(guests)}</dd></div>
				${total ? `<div><dt>${escapeHtml(__('Estimated total', 'hotel'))}</dt><dd><strong>${escapeHtml(sprintf(__('%s ₴', 'hotel'), formatPrice(total)))}</strong></dd></div>` : ''}
			</dl>
		`;
	}

	wrap.hidden = false;
	wrap.scrollIntoView({ behavior: 'smooth', block: 'start' });
	form.querySelector('input[type="text"]')?.focus();
}

export default function booking() {
	const form = document.querySelector('.booking__form');
	const results = document.querySelector('.booking__results');

	// Блока немає на сторінці, або не передано ajaxUrl/nonce (inc/booking.php).
	if (!form || !results || typeof window.hotelBooking === 'undefined') return;

	const submit = form.querySelector('.booking__submit');

	const showStatus = (message, isError = false) => {
		results.innerHTML = `<p class="booking__status${isError ? ' booking__status--error' : ''}">${escapeHtml(message)}</p>`;
	};

	// Клік «Забронювати» на картці → форма заявки з підставленими даними.
	// Делегування: картки малюються динамічно, тому слухаємо контейнер.
	results.addEventListener('click', (e) => {
		const cta = e.target.closest('.booking-card__cta');
		if (!cta) return;

		openRequestForm({
			roomId: cta.dataset.roomId,
			roomTitle: cta.dataset.roomTitle,
			roomPrice: parseFloat(cta.dataset.roomPrice) || 0,
			checkIn: form.querySelector('[name="check_in"]').value,
			checkOut: form.querySelector('[name="check_out"]').value,
			guests: form.querySelector('[name="guests"]').value,
		});
	});

	form.addEventListener('submit', async (e) => {
		e.preventDefault();

		const data = new FormData();
		data.append('action', 'hotel_check_availability');
		data.append('nonce', window.hotelBooking.nonce);
		data.append('check_in', form.querySelector('[name="check_in"]').value);
		data.append('check_out', form.querySelector('[name="check_out"]').value);
		data.append('guests', form.querySelector('[name="guests"]').value);

		submit.disabled = true;
		showStatus(__('Searching for available rooms…', 'hotel'));

		try {
			const response = await fetch(window.hotelBooking.ajaxUrl, {
				method: 'POST',
				body: data,
			});
			const json = await response.json();

			if (!json.success) {
				showStatus(json.data?.message || __('Search failed.', 'hotel'), true);
				return;
			}

			const { rooms, count } = json.data;

			if (!count) {
				showStatus(__('No rooms available for the selected dates. Try another period.', 'hotel'));
				return;
			}

			results.innerHTML = `
				<p class="booking__status">${escapeHtml(sprintf(_n('%d room available', '%d rooms available', count, 'hotel'), count))}</p>
				<div class="booking__list">${rooms.map(renderCard).join('')}</div>
			`;
		} catch (err) {
			showStatus(__('Connection error. Please try again.', 'hotel'), true);
		} finally {
			submit.disabled = false;
		}
	});
}
